import jsPDF from 'jspdf';

const COLORS = {
  dark: [15, 23, 42] as [number, number, number],
  gold: [196, 167, 125] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  lightGray: [245, 245, 245] as [number, number, number],
  gray: [156, 163, 175] as [number, number, number],
  text: [55, 65, 81] as [number, number, number],
};

const STATUS_LABELS: Record<string, string> = {
  neu: 'Neu', offen: 'Offen', in_bearbeitung: 'In Bearbeitung',
  warten_auf_rueckmeldung: 'Rückmeldung', warten_auf_dienstleister: 'Dienstleister',
  eskaliert: 'Eskaliert', erledigt: 'Erledigt', geschlossen: 'Geschlossen',
};

const PRIO_LABELS: Record<string, string> = {
  niedrig: 'Niedrig', mittel: 'Mittel', hoch: 'Hoch', kritisch: 'Kritisch',
};

interface TicketData {
  ticket_nummer: string;
  titel: string;
  beschreibung?: string | null;
  status: string;
  prioritaet: string;
  created_at: string;
  faellig_am?: string | null;
  wiedervorlage_am?: string | null;
  abschluss_grund?: string | null;
  kategorie?: { name: string } | null;
  objekt?: { bezeichnung: string; strasse?: string; hausnummer?: string } | null;
  mieter_rel?: { vorname: string; nachname: string } | null;
  zustaendig?: { full_name: string } | null;
}

interface KommentarData {
  inhalt: string;
  ist_intern: boolean;
  created_at: string;
  autor?: { full_name: string } | null;
}

interface HistorieData {
  aktion: string;
  alter_wert?: string | null;
  neuer_wert?: string | null;
  created_at: string;
  benutzer?: { full_name: string } | null;
}

interface AnhangData {
  dateiname: string;
  dateigroesse?: number | null;
  created_at: string;
}

function fmtDate(d?: string | null): string {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(d: string): string {
  const dt = new Date(d);
  return `${dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}

function fmtSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function exportSingleTicketPdf(
  ticket: TicketData,
  kommentare: KommentarData[],
  historie: HistorieData[],
  anhaenge: AnhangData[]
): void {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const datum = new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });

  const checkPage = (needed: number) => {
    if (y + needed > pageHeight - 20) {
      pdf.addPage();
      y = margin;
    }
  };

  // ── Header ──
  pdf.setFillColor(...COLORS.dark);
  pdf.rect(0, 0, pageWidth, 38, 'F');
  pdf.setFillColor(...COLORS.gold);
  pdf.rect(0, 38, pageWidth, 1.5, 'F');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(...COLORS.white);
  pdf.text(ticket.ticket_nummer, margin, 16);

  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.gold);
  pdf.text(ticket.titel.substring(0, 80), margin, 25);

  pdf.setFontSize(7);
  pdf.setTextColor(180, 180, 180);
  pdf.text(`Exportiert am ${datum}`, margin, 34);

  // Status + Prio rechts
  const statusText = STATUS_LABELS[ticket.status] || ticket.status;
  const prioText = PRIO_LABELS[ticket.prioritaet] || ticket.prioritaet;
  pdf.setFontSize(8);
  pdf.setTextColor(...COLORS.white);
  pdf.text(`${statusText}  ·  ${prioText}`, pageWidth - margin, 16, { align: 'right' });

  y = 48;

  // ── Metadaten-Grid ──
  const drawField = (label: string, value: string, x: number, w: number) => {
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.gray);
    pdf.text(label.toUpperCase(), x, y);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.text);
    pdf.text(value || '–', x, y + 5);
  };

  const half = contentWidth / 2;
  drawField('Kategorie', ticket.kategorie?.name || '–', margin, half);
  drawField('Zuständig', ticket.zustaendig?.full_name || '–', margin + half, half);
  y += 14;

  drawField('Objekt', ticket.objekt ? `${ticket.objekt.bezeichnung} · ${ticket.objekt.strasse || ''} ${ticket.objekt.hausnummer || ''}` : '–', margin, half);
  drawField('Mieter', ticket.mieter_rel ? `${ticket.mieter_rel.vorname} ${ticket.mieter_rel.nachname}` : '–', margin + half, half);
  y += 14;

  drawField('Erstellt am', fmtDate(ticket.created_at), margin, contentWidth / 3);
  drawField('Fällig am', fmtDate(ticket.faellig_am), margin + contentWidth / 3, contentWidth / 3);
  drawField('Wiedervorlage', fmtDate(ticket.wiedervorlage_am), margin + (contentWidth / 3) * 2, contentWidth / 3);
  y += 14;

  if (ticket.abschluss_grund) {
    drawField('Abschlussgrund', ticket.abschluss_grund, margin, contentWidth);
    y += 14;
  }

  // ── Divider ──
  pdf.setDrawColor(230, 230, 230);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 6;

  // ── Beschreibung ──
  if (ticket.beschreibung) {
    checkPage(20);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.dark);
    pdf.text('BESCHREIBUNG', margin, y);
    y += 5;

    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.text);
    const lines = pdf.splitTextToSize(ticket.beschreibung, contentWidth);
    for (const line of lines) {
      checkPage(5);
      pdf.text(line, margin, y);
      y += 4.5;
    }
    y += 4;
  }

  // ── Kommentare ──
  if (kommentare.length > 0) {
    checkPage(15);
    pdf.setDrawColor(230, 230, 230);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 6;

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.dark);
    pdf.text(`KOMMENTARE (${kommentare.length})`, margin, y);
    y += 7;

    for (const k of kommentare) {
      checkPage(18);

      // Author + Date
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.dark);
      pdf.text(k.autor?.full_name || 'Unbekannt', margin, y);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...COLORS.gray);
      pdf.text(fmtDateTime(k.created_at), margin + 50, y);
      if (k.ist_intern) {
        pdf.setTextColor(217, 119, 6);
        pdf.text('[INTERN]', margin + 90, y);
      }
      y += 4.5;

      // Comment text
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...COLORS.text);
      const cLines = pdf.splitTextToSize(k.inhalt, contentWidth - 4);
      for (const line of cLines) {
        checkPage(5);
        pdf.text(line, margin + 2, y);
        y += 4;
      }
      y += 4;
    }
  }

  // ── Verlauf ──
  if (historie.length > 0) {
    checkPage(15);
    pdf.setDrawColor(230, 230, 230);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 6;

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.dark);
    pdf.text(`VERLAUF (${historie.length})`, margin, y);
    y += 7;

    for (const h of historie) {
      checkPage(8);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...COLORS.gray);
      pdf.text(fmtDateTime(h.created_at), margin, y);
      pdf.setTextColor(...COLORS.text);
      const actionText = h.neuer_wert || h.aktion;
      pdf.text(`${h.benutzer?.full_name || 'System'}: ${actionText}`, margin + 35, y);
      y += 5;
    }
    y += 3;
  }

  // ── Anhänge ──
  if (anhaenge.length > 0) {
    checkPage(15);
    pdf.setDrawColor(230, 230, 230);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 6;

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.dark);
    pdf.text(`ANHÄNGE (${anhaenge.length})`, margin, y);
    y += 7;

    for (const a of anhaenge) {
      checkPage(6);
      pdf.setFontSize(7.5);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...COLORS.text);
      const sizeStr = fmtSize(a.dateigroesse);
      pdf.text(`• ${a.dateiname}${sizeStr ? ` (${sizeStr})` : ''}`, margin + 2, y);
      pdf.setTextColor(...COLORS.gray);
      pdf.text(fmtDate(a.created_at), pageWidth - margin, y, { align: 'right' });
      y += 5;
    }
  }

  // ── Footer ──
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.gray);
    pdf.text('ImmoSchwede · Ticketexport', margin, pageHeight - 8);
    pdf.text(`Seite ${i} / ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  }

  pdf.save(`${ticket.ticket_nummer}_${ticket.titel.substring(0, 30).replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, '_')}.pdf`);
}
